// apps/web/src/pages/admin/ManageUsersPage.tsx

import React, { useState, useMemo, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Users, Search, ChevronLeft, ChevronRight, Loader2, Edit, Trash2, CheckCircle, XCircle, Terminal, Eye, CircleDot, Image, FileText, Info as InfoIcon } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Role, VerificationStatus, UserOnboardingStatus, Gender, JobStatus, BookingStatus } from '@logistics-platform/types'; // Import Gender, JobStatus, BookingStatus
import { toast } from 'sonner';
import { Link } from 'react-router-dom';


// Type definition for a user returned by the API
interface AdminUser {
  id: string;
  email: string;
  phoneOne: string;
  phoneTwo?: string | null;
  role: Role;
  verificationStatus: VerificationStatus;
  onboardingStatus: UserOnboardingStatus;
  createdAt: string;
  updatedAt: string;
  profile: {
    fullName: string;
    gender?: Gender | null; // Use Gender enum type
    currentResidence?: string | null;
    organizationName?: string | null;
    nationalIdPath?: string | null; // For direct document paths
    profilePicturePath?: string | null; // For direct document paths
  } | null;
  Wallet?: {
    balance: number;
  } | null;
  vehicles?: { id: string; make: string; model: string; licensePlate: string; verified: boolean; }[];
  jobs?: { id: string; title: string; status: JobStatus; priceAmount: number; }[];
  bookings?: { id: string; status: BookingStatus; jobId: string; }[];
}

const ManageUsersPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState<VerificationStatus | undefined>(undefined);
  const [selectedOnboardingStatus, setSelectedOnboardingStatus] = useState<UserOnboardingStatus | undefined>(undefined);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data, isLoading, error, refetch } = trpc.admin.getAllUsers.useQuery({
    page,
    pageSize,
    search: debouncedSearchQuery,
    role: selectedRole,
    verificationStatus: selectedVerificationStatus,
    onboardingStatus: selectedOnboardingStatus,
    orderBy: 'createdAt',
    orderDirection: 'desc',
  }, {
    refetchInterval: 5000 // Poll every 5 seconds
  });

  const approveUserMutation = trpc.admin.approveUser.useMutation({
    onSuccess: () => { toast.success("User approved!"); refetch(); },
    onError: (err) => toast.error(err.message),
  });
  const rejectUserMutation = trpc.admin.rejectUser.useMutation({
    onSuccess: () => { toast.success("User rejected!"); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  const users = data?.users || [];
  const totalUsers = data?.totalCount || 0;
  const totalPages = Math.ceil(totalUsers / pageSize);

  // State for the Edit/Details Modal
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const { data: fullUserData, isLoading: isLoadingFullUser, error: fullUserError } = trpc.admin.getUserById.useQuery({
    userId: viewingUserId!
  }, {
    enabled: !!viewingUserId,
    staleTime: 0,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedUser, setEditedUser] = useState<AdminUser | null>(null);

  // Mutation for updating user details
  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      toast.success("User updated successfully!");
      setIsEditModalOpen(false);
      setViewingUserId(null);
      refetch(); // Refresh the main user list
    },
    onError: (err) => toast.error(err.message),
  });

  // Initialize editedUser when fullUserData is fetched
  useEffect(() => {
    if (fullUserData) {
      setEditedUser(fullUserData);
    }
  }, [fullUserData]);

  const handleViewEditClick = (userId: string) => {
    setViewingUserId(userId);
    setIsEditModalOpen(true);
  };

  const handleSaveChanges = () => {
    if (editedUser) {
      updateUserMutation.mutate({
        userId: editedUser.id,
        // Only send fields that might have changed
        email: editedUser.email,
        phoneOne: editedUser.phoneOne,
        phoneTwo: editedUser.phoneTwo,
        role: editedUser.role,
        verificationStatus: editedUser.verificationStatus,
        onboardingStatus: editedUser.onboardingStatus,
        fullName: editedUser.profile?.fullName,
        gender: editedUser.profile?.gender,
        currentResidence: editedUser.profile?.currentResidence,
        organizationName: editedUser.profile?.organizationName,
      });
    }
  };

  const handleApprove = (userId: string) => {
    approveUserMutation.mutate({ userId });
  };

  const handleReject = (userId: string, reason?: string) => {
    // You might open a dialog here to get the reason from the admin
    rejectUserMutation.mutate({ userId, reason });
  };

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-7xl">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Users</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 max-w-full animate-fade-in">
      <Card className="glass">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl"><Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />Manage All Users</CardTitle>
          <CardDescription className="text-xs sm:text-sm">View, approve, reject, and manage all registered user accounts on the K&C platform.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0 sm:pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-3 sm:gap-4 flex-wrap">
            <Input
              placeholder="Search by email, phone, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
            />
            <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-4 w-full md:w-auto">
              <Select value={selectedRole} onValueChange={(value: Role) => setSelectedRole(value)}>
                <SelectTrigger className="w-full sm:w-[150px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-popover text-popover-foreground">
                  <SelectItem value={undefined}>All Roles</SelectItem>
                  {Object.values(Role).map(role => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={selectedVerificationStatus} onValueChange={(value: VerificationStatus) => setSelectedVerificationStatus(value)}>
                <SelectTrigger className="w-full sm:w-[180px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="Verification" />
                </SelectTrigger>
                <SelectContent className="bg-popover text-popover-foreground">
                  <SelectItem value={undefined}>All Statuses</SelectItem>
                  {Object.values(VerificationStatus).map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Select value={selectedOnboardingStatus} onValueChange={(value: UserOnboardingStatus) => setSelectedOnboardingStatus(value)}>
                <SelectTrigger className="flex-1 sm:w-[180px] bg-select text-foreground h-9 sm:h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="Onboarding" />
                </SelectTrigger>
                <SelectContent className="bg-popover text-popover-foreground">
                  <SelectItem value={undefined}>All Statuses</SelectItem>
                  {Object.values(UserOnboardingStatus).map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 sm:h-10" onClick={() => { setSearchQuery(''); setSelectedRole(undefined); setSelectedVerificationStatus(undefined); setSelectedOnboardingStatus(undefined); }}>
                Reset
              </Button>
            </div>
          </div>

          {/* Users Table */}
          <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px] text-primary">ID</TableHead>
                  <TableHead className="w-[180px] text-primary">Name / Email</TableHead>
                  <TableHead className="w-[120px] text-primary">Role</TableHead>
                  <TableHead className="w-[150px] text-primary">Verification</TableHead>
                  <TableHead className="w-[120px] text-primary">Onboarding</TableHead>
                  <TableHead className="w-[120px] text-primary">Balance</TableHead>
                  <TableHead className="w-[120px] text-primary">Registered</TableHead>
                  <TableHead className="w-[180px] text-primary text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={8}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                      <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No users found matching your criteria.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="text-sm">
                      <TableCell className="font-mono text-xs">{user.id.substring(0, 8)}...</TableCell>
                      <TableCell>
                        <p className="font-medium text-foreground">{user.profile?.fullName || 'N/A'}</p>
                        <p className="text-muted-foreground text-xs">{user.email}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.role === Role.ADMIN ? "destructive" : user.role === Role.TRANSPORTER ? "secondary" : "default"}>{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.verificationStatus === VerificationStatus.VERIFIED ? "success" : user.verificationStatus === VerificationStatus.PENDING ? "warning" : "destructive"}>{user.verificationStatus}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.onboardingStatus === UserOnboardingStatus.verified ? "success" : user.onboardingStatus === UserOnboardingStatus.pending_admin_approval ? "warning" : "default"}>{user.onboardingStatus}</Badge>
                      </TableCell>
                      <TableCell className="font-medium text-foreground">MWK {user.Wallet?.balance.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{format(new Date(user.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell className="flex justify-center items-center gap-2">
                        {user.verificationStatus === VerificationStatus.PENDING && (
                          <>
                            <Button variant="success" size="sm" onClick={() => handleApprove(user.id)} disabled={approveUserMutation.isLoading}>
                              {approveUserMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleReject(user.id, "Admin review failed.")} disabled={rejectUserMutation.isLoading}>
                              {rejectUserMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleViewEditClick(user.id)} disabled={approveUserMutation.isLoading || rejectUserMutation.isLoading}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" disabled>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {Math.min(totalUsers, (page - 1) * pageSize + 1)} - {Math.min(totalUsers, page * pageSize)} of {totalUsers} users
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => prev + 1)}
                disabled={page >= totalPages}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Edit/Details Modal */}
      {editedUser && ( // Use editedUser for modal content
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-xl bg-card text-card-foreground">
            <DialogHeader>
              <DialogTitle className="text-foreground">User Details: {editedUser.profile?.fullName || editedUser.email}</DialogTitle>
              <DialogDescription>
                View comprehensive details and manage actions for this user.
              </DialogDescription>
            </DialogHeader>
            {isLoadingFullUser ? (
              <div className="py-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /> <p className="text-muted-foreground mt-2">Loading user details...</p></div>
            ) : fullUserError ? (
              <Alert variant="destructive">
                <Terminal className="h-4 w-4" />
                <AlertTitle>Error Loading User Details</AlertTitle>
                <AlertDescription>{fullUserError.message}</AlertDescription>
              </Alert>
            ) : (
              <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-primary">Basic Information</h4>
                  <p className="text-sm"><strong>ID:</strong> <span className="font-mono text-xs">{editedUser.id}</span></p>
                  <p className="text-sm"><strong>Email:</strong>
                    <Input type="email" value={editedUser.email} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, email: e.target.value }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                  </p>
                  <p className="text-sm"><strong>Phone 1:</strong>
                    <Input type="text" value={editedUser.phoneOne} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, phoneOne: e.target.value }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                  </p>
                  <p className="text-sm"><strong>Phone 2:</strong>
                    <Input type="text" value={editedUser.phoneTwo || ''} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, phoneTwo: e.target.value || null }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                  </p>
                  <p className="text-sm"><strong>Registered:</strong> {format(new Date(editedUser.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                  <p className="text-sm"><strong>Last Updated:</strong> {format(new Date(editedUser.updatedAt), 'yyyy-MM-dd HH:mm')}</p>
                </div>

                {/* Profile Info */}
                {editedUser.profile && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    <h4 className="font-semibold text-primary">Profile Details</h4>
                    <p className="text-sm"><strong>Full Name:</strong>
                      <Input type="text" value={editedUser.profile.fullName || ''} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, profile: { ...prev.profile!, fullName: e.target.value } }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                    </p>
                    <p className="text-sm"><strong>Gender:</strong>
                      <Select
                        value={editedUser.profile.gender || ''}
                        onValueChange={(value: Gender) => setEditedUser(prev => prev ? ({ ...prev, profile: { ...prev.profile!, gender: value } }) : null)}
                        disabled={updateUserMutation.isLoading}
                      >
                        <SelectTrigger className="ml-2 w-auto inline-flex bg-input text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover text-popover-foreground">
                          {Object.values(Gender).map(gender => <SelectItem key={gender} value={gender}>{gender}</SelectItem>)}
                          <SelectItem value={null as any}>N/A</SelectItem> {/* Allow setting to null */}
                        </SelectContent>
                      </Select>
                    </p>
                    <p className="text-sm"><strong>Residence:</strong>
                      <Input type="text" value={editedUser.profile.currentResidence || ''} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, profile: { ...prev.profile!, currentResidence: e.target.value || null } }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                    </p>
                    <p className="text-sm"><strong>Organization:</strong>
                      <Input type="text" value={editedUser.profile.organizationName || ''} onChange={(e) => setEditedUser(prev => prev ? ({ ...prev, profile: { ...prev.profile!, organizationName: e.target.value || null } }) : null)} className="ml-2 w-auto inline-flex" disabled={updateUserMutation.isLoading} />
                    </p>
                    {editedUser.profile.profilePicturePath && (
                      <p className="text-sm flex items-center"><strong>Profile Pic:</strong> <a href={`/uploads/${editedUser.profile.profilePicturePath}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-500 hover:underline flex items-center"><Image className="h-4 w-4 mr-1" />View</a></p>
                    )}
                    {editedUser.profile.nationalIdPath && (
                      <p className="text-sm flex items-center"><strong>National ID:</strong> <a href={`/uploads/${editedUser.profile.nationalIdPath}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-500 hover:underline flex items-center"><FileText className="h-4 w-4 mr-1" />View</a></p>
                    )}
                  </div>
                )}

                {/* Wallet Info */}
                {editedUser.Wallet && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    <h4 className="font-semibold text-primary">Wallet</h4>
                    <p className="text-sm"><strong>Balance:</strong> MWK {editedUser.Wallet.balance.toLocaleString()}</p>
                    {/* TODO: Add ability to manually adjust balance (admin action with audit log) */}
                  </div>
                )}

                {/* Statuses (Editable) */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <h4 className="font-semibold text-primary">Statuses</h4>
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label htmlFor="role-edit" className="text-right">Role</Label>
                    <Select
                      value={editedUser.role}
                      onValueChange={(value: Role) => setEditedUser(prev => prev ? ({ ...prev, role: value }) : null)}
                      disabled={updateUserMutation.isLoading}
                    >
                      <SelectTrigger id="role-edit" className="col-span-1 bg-input text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover text-popover-foreground">
                        {Object.values(Role).map(role => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Label htmlFor="ver-status-edit" className="text-right">Verification</Label>
                    <Select
                      value={editedUser.verificationStatus}
                      onValueChange={(value: VerificationStatus) => setEditedUser(prev => prev ? ({ ...prev, verificationStatus: value }) : null)}
                      disabled={updateUserMutation.isLoading}
                    >
                      <SelectTrigger id="ver-status-edit" className="col-span-1 bg-input text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover text-popover-foreground">
                        {Object.values(VerificationStatus).map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Label htmlFor="onb-status-edit" className="text-right">Onboarding</Label>
                    <Select
                      value={editedUser.onboardingStatus}
                      onValueChange={(value: UserOnboardingStatus) => setEditedUser(prev => prev ? ({ ...prev, onboardingStatus: value }) : null)}
                      disabled={updateUserMutation.isLoading}
                    >
                      <SelectTrigger id="onb-status-edit" className="col-span-1 bg-input text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover text-popover-foreground">
                        {Object.values(UserOnboardingStatus).map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Vehicles (Read-only list, link to full vehicle page) */}
                {editedUser.vehicles && editedUser.vehicles.length > 0 && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    <h4 className="font-semibold text-primary">Vehicles ({editedUser.vehicles.length})</h4>
                    <ul className="text-sm list-disc pl-5">
                      {editedUser.vehicles.map(vehicle => (
                        <li key={vehicle.id} className="text-muted-foreground">{vehicle.make} {vehicle.model} (<Link to={`/admin/vehicles?search=${vehicle.licensePlate}`} className="text-blue-500 hover:underline">{vehicle.licensePlate}</Link>) - {vehicle.verified ? 'Verified' : 'Pending'}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Jobs (Read-only list, link to full job page) */}
                {editedUser.jobs && editedUser.jobs.length > 0 && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    <h4 className="font-semibold text-primary">Jobs Posted ({editedUser.jobs.length})</h4>
                    <ul className="text-sm list-disc pl-5">
                      {editedUser.jobs.map(job => (
                        <li key={job.id} className="text-muted-foreground">"{job.title}" - {job.status} (MWK {job.priceAmount.toLocaleString()}) <Link to={`/admin/jobs?search=${job.title}`} className="text-blue-500 hover:underline">View Job</Link></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setViewingUserId(null); setEditedUser(null); }} disabled={updateUserMutation.isLoading}>Close</Button>
              <Button onClick={handleSaveChanges} disabled={updateUserMutation.isLoading}>
                {updateUserMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ManageUsersPage;